import React from "react";
import { Box, Tooltip, useMediaQuery } from "@mui/material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { PublicServiceRequestMachine } from "../../../serviceBoard/models/models";

interface RfsMachineCardProps {
  machine: PublicServiceRequestMachine;
  isDecommissioned?: boolean;
}

/** Key-value spec row used inside the machine card */
const SpecRow = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: "8px",
      borderBottom: "1px solid #F3F4F6",
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: dt.font,
        color: "#6B7280",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: dt.fontMono,
        color: "#1E293B",
        textAlign: "right",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "60%",
      }}
    >
      {value || "\u2014"}
    </Box>
  </Box>
);

const RfsMachineCard = ({ machine, isDecommissioned }: RfsMachineCardProps) => {
  const { translate } = useTranslation(["serviceBoard", "servicing"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  // Decommissioned fallback
  if (isDecommissioned) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          backgroundColor: "#FEF9E7",
          borderLeft: "3px solid #F39C12",
          borderRadius: "8px",
          padding: "12px 16px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: "20px", color: "#F39C12" }} />
          <Box sx={{ fontSize: "13px", color: "#92400E", fontWeight: 500, fontFamily: dt.font }}>
            {translate("servicing:decommissionedMachine.title")}
          </Box>
        </Box>
        {machine?.name && (
          <Box sx={{ fontSize: "13px", color: "#92400E", fontFamily: dt.font }}>
            {machine.name}
          </Box>
        )}
      </Box>
    );
  }

  const imageUrl = machine.imageUrl || machine.catalogImageUrl;
  const systemDisplay = [machine.systemBrandName, machine.system].filter(Boolean).join(" ");

  // Phone: horizontal compact card
  if (isPhone) {
    return (
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          border: `1px solid ${dt.cardBorder}`,
          borderRadius: "14px",
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {/* Photo */}
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={machine.name}
            sx={{
              width: 48,
              height: 48,
              borderRadius: dt.radiusSm,
              objectFit: "cover",
              border: `1px solid ${dt.cardBorder}`,
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: dt.radiusSm,
              backgroundColor: "#F4F6F9",
              border: `1px solid ${dt.cardBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 22, color: dt.textMuted }} />
          </Box>
        )}

        {/* Info */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={machine.name || ""} enterDelay={400} arrow>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: "#1E293B",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {machine.name}
            </Box>
          </Tooltip>
          <Box
            sx={{
              fontSize: "12px",
              fontFamily: dt.fontMono,
              fontWeight: 400,
              color: dt.textSecondary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: "2px",
            }}
          >
            {[
              machine.serialNumber ? `S/N: ${machine.serialNumber}` : null,
              machine.brandName,
              systemDisplay,
            ]
              .filter(Boolean)
              .join(" | ")}
          </Box>
        </Box>
      </Box>
    );
  }

  // Desktop / Tablet: vertical card
  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      {/* Photo section */}
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={machine.name}
          sx={{
            width: "100%",
            height: "auto",
            maxHeight: 200,
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: 140,
            backgroundColor: "#F4F6F9",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
          }}
        >
          <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 36, color: dt.textMuted, opacity: 0.5 }} />
          <Box sx={{ fontSize: "11px", fontFamily: dt.font, fontWeight: 500, color: dt.textMuted }}>
            {translate("serviceBoard:noImage")}
          </Box>
        </Box>
      )}

      {/* Info section */}
      <Box sx={{ padding: "16px" }}>
        <Tooltip title={machine.name || ""} enterDelay={400} arrow>
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              fontFamily: dt.font,
              color: "#1E293B",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {machine.name}
          </Box>
        </Tooltip>
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            fontFamily: dt.font,
            color: "#6B7280",
            mt: "2px",
          }}
        >
          {machine.machineCategoryName || machine.model}
        </Box>

        {/* Key-value rows */}
        <Box sx={{ mt: 1.5 }}>
          <SpecRow label={translate("serviceBoard:machineSerialNumber")} value={machine.serialNumber || ""} />
          {machine.internalNumber && (
            <SpecRow label={translate("serviceBoard:machineNumber")} value={`#${machine.internalNumber}`} />
          )}
          <SpecRow label={translate("serviceBoard:machineBrandLabel")} value={machine.brandName || ""} />
          {systemDisplay && (
            <SpecRow label={translate("serviceBoard:machineSystem")} value={systemDisplay} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RfsMachineCard;
